import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, ArrowLeft, ArrowRight, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateFinancial() {
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      financial: saved?.financial || {
        fundingAsk: 0,
        useOfFunds: {
          product: 0,
          marketing: 0,
          team: 0,
          operations: 0,
          legal: 0,
          other: 0,
        },
        runwayMonths: 0,
        burnRate: 0,
        revenueProjection: {
          month1: 0,
          month3: 0,
          month6: 0,
          month12: 0,
        },
        breakevenMonth: 0,
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/competition');
  };

  const handleNext = () => {
    navigate('/pitch/new/gtm');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const updateUseOfFunds = (field: string, value: number) => {
    setForm(prev => ({
      ...prev,
      financial: {
        ...prev.financial,
        useOfFunds: {
          ...prev.financial.useOfFunds,
          [field]: value
        }
      }
    }));
  };

  const updateRevenue = (field: string, value: number) => {
    setForm(prev => ({
      ...prev,
      financial: {
        ...prev.financial,
        revenueProjection: {
          ...prev.financial.revenueProjection,
          [field]: value
        }
      }
    }));
  };

  const totalUseOfFunds = Object.values(form.financial.useOfFunds).reduce((a, b) => a + b, 0);

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Financial Projections</h1>
            <p className="text-muted-foreground mt-1">Step 4 of 8 · Show us the numbers</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Funding & Financials</CardTitle>
            <CardDescription>
              Be realistic. Pre-seed investors know you're guessing, but they want to see you've thought it through.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-blue-50 border-blue-200">
              <InfoIcon className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                Your numbers should tell a coherent story. If you're asking for $500k, where is it going?
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-lg font-medium">Funding Ask ($)</Label>
                <Input
                  type="number"
                  value={form.financial.fundingAsk}
                  onChange={e => setForm({
                    ...form,
                    financial: {
                      ...form.financial,
                      fundingAsk: parseInt(e.target.value) || 0
                    }
                  })}
                  min="0"
                  className="text-base py-6"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-medium">Runway (months)</Label>
                <Input
                  type="number"
                  value={form.financial.runwayMonths}
                  onChange={e => setForm({
                    ...form,
                    financial: {
                      ...form.financial,
                      runwayMonths: parseInt(e.target.value) || 0
                    }
                  })}
                  min="0"
                  className="text-base py-6"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-medium">Monthly Burn Rate ($)</Label>
                <Input
                  type="number"
                  value={form.financial.burnRate}
                  onChange={e => setForm({
                    ...form,
                    financial: {
                      ...form.financial,
                      burnRate: parseInt(e.target.value) || 0
                    }
                  })}
                  min="0"
                  className="text-base py-6"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-lg font-medium">Breakeven Month (from now)</Label>
                <Input
                  type="number"
                  value={form.financial.breakevenMonth}
                  onChange={e => setForm({
                    ...form,
                    financial: {
                      ...form.financial,
                      breakevenMonth: parseInt(e.target.value) || 0
                    }
                  })}
                  min="0"
                  className="text-base py-6"
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-lg font-medium">Use of Funds (%)</Label>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm">Product Development</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.product}
                    onChange={e => updateUseOfFunds('product', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Marketing & Sales</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.marketing}
                    onChange={e => updateUseOfFunds('marketing', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Team & Hiring</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.team}
                    onChange={e => updateUseOfFunds('team', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Operations</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.operations}
                    onChange={e => updateUseOfFunds('operations', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Legal & Admin</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.legal}
                    onChange={e => updateUseOfFunds('legal', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Other</Label>
                  <Input
                    type="number"
                    value={form.financial.useOfFunds.other}
                    onChange={e => updateUseOfFunds('other', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="text-base py-6"
                  />
                </div>
              </div>
              <p className={`text-sm ${totalUseOfFunds !== 100 ? 'text-red-500' : 'text-green-600'}`}>
                Total: {totalUseOfFunds}% {totalUseOfFunds !== 100 && '(should total 100%)'}
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-lg font-medium">Revenue Projections ($)</Label>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Month 1</Label>
                  <Input
                    type="number"
                    value={form.financial.revenueProjection.month1}
                    onChange={e => updateRevenue('month1', parseInt(e.target.value) || 0)}
                    min="0"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Month 3</Label>
                  <Input
                    type="number"
                    value={form.financial.revenueProjection.month3}
                    onChange={e => updateRevenue('month3', parseInt(e.target.value) || 0)}
                    min="0"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Month 6</Label>
                  <Input
                    type="number"
                    value={form.financial.revenueProjection.month6}
                    onChange={e => updateRevenue('month6', parseInt(e.target.value) || 0)}
                    min="0"
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Month 12</Label>
                  <Input
                    type="number"
                    value={form.financial.revenueProjection.month12}
                    onChange={e => updateRevenue('month12', parseInt(e.target.value) || 0)}
                    min="0"
                    className="text-base py-6"
                  />
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: Competition
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: Go-to-Market
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}